import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import NotionRenderer from '@famiryru/react-notion-renderer'
import { BlockType } from '@famiryru/react-notion-renderer/lib/types'
import {
  SimpleTaskStatusType,
  SimpleTaskType,
} from '../../containers/LessonPage/types'
import { useDispatch } from 'react-redux'
import * as actionsLesson from '../../containers/LessonPage/actions'
import { getFormatFromUrl } from '../../utils/helpers'

// Styles
import './styles.scss'

// Components
import Loading from '../Loading'
import notification from '../Notification'

// Icons
import IconAttach from './images/iconAttach'
import IconCancel from './images/iconCancel'
import IconFile from './images/iconFile'

const allowedAttachFormats = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'jpg',
  'png',
  'pptx',
  'ppt',
  'pps',
  'ppsx',
  'mpg',
  'mov',
  'wmv',
  'avi',
  '3gp',
  'mkv',
  'mp4',
  'asf',
  'mpeg',
]

export default ({
  simpleTask,
  simpleTaskStatus,
  courseId,
  moduleId,
  lessonId,
  isProcess,
  isProcessJoin,
}: {
  simpleTask: SimpleTaskType
  simpleTaskStatus: SimpleTaskStatusType
  courseId: string
  moduleId: string
  lessonId: string
  isProcess: boolean
  isProcessJoin: boolean
}) => {
  const [attach, setAttach] = useState<File | null>(null)
  const [field, setField] = useState<string>('')
  const [open, setOpen] = useState<boolean>(false)
  const [isRequired, setIsRequired] = useState<boolean>(false)
  const [uploaded, setUploaded] = useState<{
    field: string
    url: string
  } | null>(null)
  const [notionDescription, setNotionDescription] = useState<BlockType[]>([])

  const dispatch = useDispatch()

  const refAttach = useRef<HTMLInputElement | null>(null)

  const isEditTask =
    simpleTask.task.user_simple_task?.status === 'waiting_for_review'
  const isFinishedTask = simpleTask.task.user_simple_task?.status === 'approved'

  useEffect(() => {
    if (simpleTask.task.user_simple_task) {
      const userSimpleTask = simpleTask.task.user_simple_task
      if (simpleTask.task.notion_page_id && simpleTask.task.fetched) {
        setNotionDescription([
          ...JSON.parse(simpleTask.task.notion_page_content),
        ])
      }
      if (userSimpleTask) {
        setField(userSimpleTask.field_value || '')
        if (userSimpleTask.file) {
          const url = userSimpleTask.file?.url
          const filename = url.substring(url.lastIndexOf('/') + 1)
          setUploaded({
            field: filename,
            url: url,
          })
        }
      }
    } else {
      setOpen(true)
    }
  }, [simpleTask, simpleTaskStatus])

  const onChangeField = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setField(event.target.value)
  }

  const handleJoinSimpleTask = () => {
    dispatch(
      actionsLesson.joinSimpleTask(
        simpleTask.simple_task_id,
        courseId,
        moduleId,
        lessonId
      )
    )
  }

  const handleAttachSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0]
      const format = file ? getFormatFromUrl(file.name) : false
      if (format && allowedAttachFormats.includes(format.toLowerCase())) {
        setAttach(file)
      } else {
        notification(
          'error',
          'Неверный формат файла',
          `Допустимые форматы: ${allowedAttachFormats.join(', ')}`
        )
      }
      if (refAttach.current) {
        refAttach.current.value = ''
      }
    }
  }

  const handleSendSimpleTask = () => {
    if (
      (simpleTaskStatus.field_required && !field.length) ||
      (simpleTaskStatus.file_required && !attach && !uploaded?.url)
    ) {
      setIsRequired(true)
      return
    }

    dispatch(
      actionsLesson.sendSimpleTask(
        courseId,
        moduleId,
        lessonId,
        simpleTask.simple_task_id,
        field || undefined,
        attach
      )
    )
  }

  return (
    <div
      className={
        isEditTask || isFinishedTask ? 'simple-task edit' : 'simple-task'
      }
    >
      {simpleTask.task.user_simple_task ? (
        open ? (
          <>
            <div className={'simple-task__joined-header'}>
              <div className={'title'}>
                {simpleTask.task.caption}{' '}
                {isRequired &&
                ((simpleTaskStatus.file_required && !attach) ||
                  (simpleTaskStatus.field_required && !field.length)) ? (
                  <span className={'required'}> *</span>
                ) : null}
              </div>
              <button
                className={
                  isEditTask || isFinishedTask
                    ? 'close-button edit'
                    : 'close-button'
                }
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>
            <div className={'simple-task__joined-text'}>
              {notionDescription.length ? (
                <NotionRenderer blocks={notionDescription} />
              ) : (
                simpleTask.task.description
              )}
            </div>
            {simpleTaskStatus.show_field ? (
              <>
                <div className={'simple-task__joined-caption'}>
                  {simpleTaskStatus.field_caption}{' '}
                  {isRequired &&
                  simpleTaskStatus.field_required &&
                  !field.length ? (
                    <span className={'required'}>*</span>
                  ) : null}
                </div>
                <textarea
                  value={field}
                  onChange={onChangeField}
                  className={'simple-task__joined-textarea'}
                  maxLength={2000}
                  readOnly={isFinishedTask}
                />
              </>
            ) : null}
            <div className={'simple-task__joined-buttons'}>
              {isFinishedTask ? null : (
                <button
                  disabled={isProcess}
                  className={isEditTask ? 'send edit' : 'send'}
                  onClick={handleSendSimpleTask}
                >
                  {isProcess ? (
                    <Loading style={{ margin: 0 }} />
                  ) : isEditTask ? (
                    'Сохранить'
                  ) : (
                    'Отправить'
                  )}
                </button>
              )}
              <div className={'attach'}>
                <input
                  ref={refAttach}
                  type={'file'}
                  onChange={handleAttachSelect}
                  accept={
                    '.pdf, .doc, .docx, .xls, .xlsx, .jpg, .png, .pptx, .ppt, .pps, .ppsx, .mpg, .mov, .wmv, .avi, .3gp, .mkv, .mp4, .asf, .mpeg'
                  }
                />
                {uploaded && isFinishedTask ? (
                  <>
                    <button
                      className={'attach__icon'}
                      style={{
                        background:
                          isEditTask || isFinishedTask ? '#219321' : '#F4BA27',
                      }}
                      onClick={() => window.open(uploaded.url, '_blank')}
                    >
                      <IconFile />
                    </button>
                    <div className={'attach__text'}>{uploaded.field}</div>
                  </>
                ) : simpleTaskStatus.show_file && !isFinishedTask ? (
                  <>
                    <div
                      className={'attach__icon'}
                      onClick={() =>
                        uploaded?.url
                          ? window.open(uploaded.url, '_blank')
                          : refAttach.current?.click()
                      }
                      style={
                        attach || isEditTask
                          ? {
                              background: isEditTask ? '#219321' : '#F4BA27',
                            }
                          : {}
                      }
                    >
                      {attach || uploaded?.url ? (
                        <IconFile />
                      ) : (
                        <IconAttach color={isEditTask ? '#FFF' : ''} />
                      )}
                    </div>
                    <div className={'attach__text'}>
                      {attach || uploaded?.url ? (
                        <>
                          Файл
                          <br />
                          прикреплен
                          <br />
                          <button
                            className={'cancel'}
                            onClick={() => {
                              setAttach(null)
                              setUploaded({
                                field: '',
                                url: '',
                              })
                            }}
                          >
                            Удалить <IconCancel style={{ marginLeft: 10 }} />
                          </button>
                        </>
                      ) : (
                        <>
                          {simpleTaskStatus.file_caption || 'Прикрепить файл'}
                          {isRequired &&
                          simpleTaskStatus.file_required &&
                          !attach ? (
                            <span className={'required'}> *</span>
                          ) : null}
                        </>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <div
            className={
              isEditTask || isFinishedTask
                ? 'simple-task__header edit'
                : 'simple-task__header'
            }
          >
            <div className={'title'}>
              {isEditTask || isFinishedTask ? (
                <>
                  МОЛОДЕЦ!
                  <br />
                  ТЫ ВЫПОЛНИЛ
                  <br />
                  ЗАДАНИЕ
                </>
              ) : (
                <>
                  ТЕПЕРЬ ТЫ
                  <br />
                  ГОТОВ! ВЫПОЛНИ ЗАДАНИЕ
                </>
              )}
            </div>
            <button onClick={() => setOpen(true)}>
              {isEditTask && !isFinishedTask ? 'Редактировать' : 'Показать'}
            </button>
          </div>
        )
      ) : (
        <div className={'simple-task__header'}>
          <div className={'title'}>
            ТЕПЕРЬ ТЫ
            <br />
            ГОТОВ! ВЫПОЛНИ ЗАДАНИЕ
          </div>
          <button disabled={isProcessJoin} onClick={handleJoinSimpleTask}>
            {isProcessJoin ? (
              <Loading
                color={'#FFF'}
                width={60}
                height={60}
                style={{ margin: '-4px 0 0 0' }}
              />
            ) : (
              'Задание'
            )}
          </button>
        </div>
      )}
    </div>
  )
}
